import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import api from '../../service/api';

export const fetchUsers = createAsyncThunk('users/list', async (params: any) => {
    const response = await api.get('users/list', { params });
    return response.data;
});

export const fetchUserById = createAsyncThunk('users/get', async (id: string) => {
    const response = await api.get(`users/${id}/get`);
    return response.data;
});

export const fetchUpdateUser = createAsyncThunk('users/update', async (params: any) => {
    const {id, data} = params;
    const response = await api.post(`users/${id}/update`, convertDataToServer(data));
    return response.data;
});

export const fetchUserCreate = createAsyncThunk('users/create', async (params: any) => {
    const response = await api.post('users/create', convertDataToServer(params));
    return response.data;
});

interface  ListFilters {
    order_by: string,
    order_dir: string,
    page: number,
    ipp: number,
    first_name: string,
    last_name: string,
    role: string,
    email: string,
    is_active: number,
}

interface  Pagination {
    total: number,
    current_page: number,
    last_page: number,
    per_page: number,
    from: number,
    to: number,
}

interface UserState {
    users: Array<object>;
    user: object | null;
    listFilters: ListFilters;
    pagination: Pagination;
    isLoading: boolean;
}

const initialState: UserState = {
    users: [],
    user: null,
    listFilters: {
        order_by: 'first_name',
        order_dir: 'asc',
        page: 1,
        ipp: 25,
        first_name: '',
        last_name: '',
        role: '',
        email: '',
        is_active: 1,
    },
    pagination: {
        total: 0,
        current_page: 1,
        last_page: 1,
        per_page: 1,
        from: 1,
        to: 1,
    },
    isLoading: false,
};

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setFilterParams: (state, action) => {
            state.listFilters = action.payload;
        },
        resetFilters: (state) => {
            state.listFilters = initialState.listFilters;
        },
        setUser: (state, action) => {
            state.user = action.payload;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchUsers.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchUsers.fulfilled, (state: any, action: any) => {
                const {data, current_page, total, last_page, ipp, per_page, from, to} = action.payload.data.resource;
                state.users = data;
                state.pagination = {
                    ...state.pagination,
                    current_page: current_page,
                    total: total,
                    last_page: last_page,
                    ipp: ipp,
                    page: current_page,
                    per_page: per_page,
                    from: from,
                    to: to,
                }
                state.isLoading = false;
            })
            .addCase(fetchUsers.rejected, state => {
                state.isLoading = false;
            })
            .addCase(fetchUserById.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchUserById.fulfilled, (state: any, action: any) => {
                state.user = action.payload.data.resource;
                state.isLoading = false;
            })
            .addCase(fetchUserById.rejected, state => {
                state.isLoading = false;
            })
            .addCase(fetchUpdateUser.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchUpdateUser.fulfilled, (state: any, action: any) => {
                state.user = action.payload.data.resource;
                state.isLoading = false;
            })
            .addCase(fetchUpdateUser.rejected, state => {
                state.isLoading = false;
            })
            .addCase(fetchUserCreate.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchUserCreate.fulfilled, (state: any, action: any) => {
                state.user = action.payload.data.resource;
                state.isLoading = false;
            })
            .addCase(fetchUserCreate.rejected, state => {
                state.isLoading = false;
            })
            .addDefaultCase(() => {
            });
    },
});

const convertDataToServer = (data: any) => {
    const ret: any = {
        user: {
            email: data.email,
            role: data.role,
            is_active: data.is_active,
            ...(data.password && {password: data.password})
        },
        profile: {
            first_name: data.first_name,
            last_name: data.last_name,
        }
    };

    return ret;
}

export const { setFilterParams, resetFilters, setUser } = userSlice.actions;

export default userSlice.reducer;
